import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Form,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function ContentPreferences() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Topics with their thesis/outcome selections
  const [topics, setTopics] = useState([]);

  // Available options for dropdowns
  const [thesisOptions, setThesisOptions] = useState([]);
  const [outcomeOptions, setOutcomeOptions] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Get all options for dropdowns
      const optionsRes = await fetch('/api/content/all-options', { credentials: 'include' });
      if (optionsRes.ok) {
        const data = await optionsRes.json();
        // Flatten into simple lists
        const theses = data.all_theses?.map(t => t.thesis) || [];
        const outcomes = data.all_outcomes?.map(o => o.outcome) || [];
        setThesisOptions(theses);
        setOutcomeOptions(outcomes);
      }

      // Generate initial topic suggestions
      await generateTopics();

    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateTopics = async () => {
    try {
      setGenerating(true);
      const res = await fetch('/api/content/generate-topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ count: 5 }),
      });

      if (res.ok) {
        const data = await res.json();
        // Transform topics to include editable fields and selection state
        const transformedTopics = (data.topics || []).map((topic, idx) => ({
          id: idx,
          // Original AI suggestion (for learning)
          originalTopic: topic.topic,
          originalThesis: topic.suggested_theses?.[0] || '',
          originalOutcome: topic.suggested_outcomes?.[0] || '',
          // User's editable values (starts same as original)
          topic: topic.topic,
          chosenThesis: topic.suggested_theses?.[0] || '',
          chosenOutcome: topic.suggested_outcomes?.[0] || '',
          // Angle/description (support both old and new field names)
          angle: topic.angle,
          trend_signal: topic.trend_signal,
          why_it_matters: topic.why_it_matters,
          // Whether to create for month 1
          createForMonth1: false,
          // Other suggestions available for dropdowns
          otherTheses: topic.suggested_theses || [],
          otherOutcomes: topic.suggested_outcomes || [],
        }));
        setTopics(transformedTopics);
      } else {
        setError('Failed to generate topics');
      }
    } catch (err) {
      setError('Failed to generate topics');
    } finally {
      setGenerating(false);
    }
  };

  const updateTopic = (id, field, value) => {
    setTopics(prev => prev.map(t =>
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const selectThesisFromDropdown = (topicId, thesis) => {
    // When selecting from dropdown, put it in the editable field
    updateTopic(topicId, 'chosenThesis', thesis);
  };

  const selectOutcomeFromDropdown = (topicId, outcome) => {
    updateTopic(topicId, 'chosenOutcome', outcome);
  };

  const saveAndContinue = async () => {
    const selectedTopics = topics.filter(t => t.createForMonth1);

    if (selectedTopics.length === 0) {
      // Just save learnings without creating drafts
      await saveLearnings();
      navigate('/command-center/campaign-hq/social/long-form');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Save learnings from all topics (even unselected ones teach us)
      await saveLearnings();

      // Create drafts for selected topics
      for (const topic of selectedTopics) {
        await fetch('/api/content/drafts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            topic: topic.topic,  // Use the (possibly edited) topic title
            thesis: topic.chosenThesis,
            outcome: topic.chosenOutcome,
            platform: 'blog',
          }),
        });
      }

      setSuccess(`Creating ${selectedTopics.length} posts in background...`);
      setTimeout(() => {
        navigate('/command-center/campaign-hq/social/long-form');
      }, 1500);

    } catch (err) {
      setError('Failed to save selections');
    } finally {
      setSaving(false);
    }
  };

  const saveLearnings = async () => {
    // Send all topic selections to learn from (original vs chosen)
    const learnings = topics.map(t => ({
      suggested_thesis: t.originalThesis,
      chosen_thesis: t.chosenThesis,
      suggested_outcome: t.originalOutcome,
      chosen_outcome: t.chosenOutcome,
      was_modified: t.chosenThesis !== t.originalThesis || t.chosenOutcome !== t.originalOutcome,
    }));

    await fetch('/api/content/learn-preferences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ learnings }),
    });
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading content planning...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="mb-4">
        <h1>Content Planning</h1>
        <p className="text-muted">
          Review these topic suggestions. Tweak the title, thesis, and outcome to match your voice.
          The more you refine, the better we learn your style.
        </p>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success">{success}</Alert>
      )}

      {generating ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Generating topic suggestions based on your profile...</p>
        </div>
      ) : (
        <>
          {topics.map((topic) => (
            <Card key={topic.id} className="mb-4 shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Form.Check
                  type="checkbox"
                  id={`month1-${topic.id}`}
                  label={<strong>Create for Month 1</strong>}
                  checked={topic.createForMonth1}
                  onChange={(e) => updateTopic(topic.id, 'createForMonth1', e.target.checked)}
                />
              </Card.Header>
              <Card.Body>
                {/* Editable Topic Title */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Topic Title</strong>
                    {topic.topic !== topic.originalTopic && (
                      <span className="text-success ms-2 small">(edited)</span>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={topic.topic}
                    onChange={(e) => updateTopic(topic.id, 'topic', e.target.value)}
                    style={{ fontWeight: '500' }}
                  />
                </Form.Group>

                <p className="text-muted small mb-3">
                  {topic.trend_signal || topic.angle}
                  {topic.why_it_matters && <span className="d-block mt-1"><em>Why now:</em> {topic.why_it_matters}</span>}
                </p>

                <Row>
                  {/* Strategic Thesis - Dropdown + Editable */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <strong>Strategic Thesis</strong>
                        <span className="text-muted ms-2 small">(core argument)</span>
                      </Form.Label>
                      <Form.Select
                        className="mb-2"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            selectThesisFromDropdown(topic.id, e.target.value);
                          }
                        }}
                      >
                        <option value="">Select a thesis to start with...</option>
                        {/* Show topic-specific suggestions first */}
                        {topic.otherTheses.length > 0 && (
                          <optgroup label="Suggested for this topic">
                            {topic.otherTheses.map((t, i) => (
                              <option key={`sugg-${i}`} value={t}>{t}</option>
                            ))}
                          </optgroup>
                        )}
                        {/* Then all options */}
                        <optgroup label="All theses">
                          {thesisOptions.map((t, i) => (
                            <option key={i} value={t}>{t}</option>
                          ))}
                        </optgroup>
                      </Form.Select>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={topic.chosenThesis}
                        onChange={(e) => updateTopic(topic.id, 'chosenThesis', e.target.value)}
                        placeholder="Select above or type your own thesis..."
                      />
                      {topic.chosenThesis !== topic.originalThesis && topic.originalThesis && (
                        <Form.Text className="text-success">
                          Modified from original
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Desired Outcome - Dropdown + Editable */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <strong>Desired Outcome</strong>
                        <span className="text-muted ms-2 small">(reader shift)</span>
                      </Form.Label>
                      <Form.Select
                        className="mb-2"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            selectOutcomeFromDropdown(topic.id, e.target.value);
                          }
                        }}
                      >
                        <option value="">Select an outcome to start with...</option>
                        {/* Show topic-specific suggestions first */}
                        {topic.otherOutcomes.length > 0 && (
                          <optgroup label="Suggested for this topic">
                            {topic.otherOutcomes.map((o, i) => (
                              <option key={`sugg-${i}`} value={o}>{o}</option>
                            ))}
                          </optgroup>
                        )}
                        {/* Then all options */}
                        <optgroup label="All outcomes">
                          {outcomeOptions.map((o, i) => (
                            <option key={i} value={o}>{o}</option>
                          ))}
                        </optgroup>
                      </Form.Select>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={topic.chosenOutcome}
                        onChange={(e) => updateTopic(topic.id, 'chosenOutcome', e.target.value)}
                        placeholder="Select above or type your own outcome..."
                      />
                      {topic.chosenOutcome !== topic.originalOutcome && topic.originalOutcome && (
                        <Form.Text className="text-success">
                          Modified from original
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))}

          <div className="d-flex justify-content-between align-items-center mt-4">
            <Button
              variant="outline-secondary"
              onClick={generateTopics}
              disabled={generating}
            >
              Regenerate Topics
            </Button>

            <div>
              <span className="me-3 text-muted">
                {topics.filter(t => t.createForMonth1).length} topics selected for Month 1
              </span>
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => navigate('/command-center/campaign-hq/social/long-form')}
              >
                Skip for Now
              </Button>
              <Button
                onClick={saveAndContinue}
                disabled={saving}
                style={{
                  background: colors.goldMid,
                  border: 'none',
                  color: colors.greyDarkest,
                }}
              >
                {saving ? 'Saving...' : 'Save & Continue'}
              </Button>
            </div>
          </div>

          <Alert variant="info" className="mt-4">
            <strong>How this helps:</strong> Every tweak you make teaches us your preferences.
            Over time, our suggestions will match your style perfectly.
          </Alert>
        </>
      )}
    </Container>
  );
}

export default ContentPreferences;
